import React, { useEffect, useState } from 'react';
import { isFeatureEnabled, onFeatureOverridesChange } from 'utils/feature-flags';
import type { FeatureGatedContentProps } from './feature-gated-content-props';

/**
 * Renders children only if the specified feature flag(s) are enabled.
 *
 * Supports single flag or multiple flags (ALL must be enabled by default).
 * Can be inverted to show content when flag(s) are disabled.
 *
 * @example
 * // Show content only when 'admin' flag is enabled
 * <FeatureGatedContent flag="admin">
 *   <AdminSection />
 * </FeatureGatedContent>
 *
 * @example
 * // Show content when ALL flags are enabled
 * <FeatureGatedContent flag={['admin', 'newUI']}>
 *   <NewPaymentUI />
 * </FeatureGatedContent>
 *
 * @example
 * // Show content when flag is disabled (inverted)
 * <FeatureGatedContent flag="newFeature" inverted>
 *   <LegacyFeature />
 * </FeatureGatedContent>
 */
export const FeatureGatedContent: React.FC<FeatureGatedContentProps> = ({
  flag,
  inverted = false,
  children,
}) => {
  const [shouldRender, setShouldRender] = useState<boolean>(false);

  /**
   * Evaluates whether content should render based on flag state(s).
   */
  const evaluateFlags = (): boolean => {
    // If no flag specified, always render
    if (!flag) {
      return true;
    }

    // Handle single flag
    if (typeof flag === 'string') {
      const enabled = isFeatureEnabled(flag);
      return inverted ? !enabled : enabled;
    }

    // Handle multiple flags - ALL must be enabled (AND logic)
    if (Array.isArray(flag)) {
      const allEnabled = flag.every((f) => isFeatureEnabled(f));
      return inverted ? !allEnabled : allEnabled;
    }

    return false;
  };

  /**
   * Updates render state when flags change or component mounts.
   */
  useEffect(() => {
    const updateRenderState = () => {
      setShouldRender(evaluateFlags());
    };

    // Initial evaluation
    updateRenderState();

    // Subscribe to flag override changes
    const unsubscribe = onFeatureOverridesChange(updateRenderState);

    return () => {
      unsubscribe();
    };
  }, [flag, inverted]);

  return <>{shouldRender && children}</>;
};
